// @flow
import * as React from 'react';
import {
  DataGrid, DataGridColumn,
  ListPageWrapper,
  PageTitle,
  useSessionStorageState
} from '../../shared';
import { useManuallyUploadedPlanFilesQuery } from '@coverright/data-access/medicare';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { SearchInput, SelectInput } from '@coverright/ui/inputs';
import { CircularProgress, IconButton, MenuItem, Stack } from '@mui/material';
import { useState } from 'react';
import { environment } from '../../../environments/environment';
import { enqueueSnackbar } from 'notistack';
import { Check, FilePlus } from '@phosphor-icons/react';
import { TableSettings, useTableSettings, withTableSettings } from '../../features';
import { ManuallyUploadedPlanFileOutput } from '@coverright/data-access/types/medicare';
import { PageInput, PlanYear } from '@coverright/data-access/types/enrollment';

type Filters = {planYear?: PlanYear | '', bidId?: string}
const initialFilters: Filters = {bidId: '', planYear: ''};

const Page = () => {
  const [page, setPage] = usePage();
  const [filters, setFilters] = useFilters();
  const {columns} = useTableSettings(tableColumns, filters, settings => setFilters(settings.filters || initialFilters));

  const {loading, data} = useManuallyUploadedPlanFilesQuery({
    client: mainBackendApolloClient,
    variables: {
      page,
      filter: {planYear: filters.planYear || undefined, bidId: filters.bidId}
    }
  })

  const rows = data?.manuallyUploadedPlanFiles?.data || [];
  const total = data?.manuallyUploadedPlanFiles?.totalElements ? parseFloat(String(data?.manuallyUploadedPlanFiles?.totalElements)) : 0;

  return (
    <ListPageWrapper>
      <PageTitle title="Plan files list">
        <TableSettings />

        <Stack gap={2} direction={'row'} alignItems={'center'}>
          <SearchInput value={filters.bidId || ''}
                       loading={loading}
                       placeholder={'Bid ID'}
                       onChange={v => setFilters(prev => ({...prev, bidId: v}))} />

          <SelectInput placeholder={'Plan year'}
                       className={'mt-0'}
                       value={filters.planYear}
                       onChange={event => {
                         setFilters(prev => ({...prev, planYear: event.target.value as PlanYear || null}))
                       }}
                       hideTick
          >
            <MenuItem value={''}>-</MenuItem>
            <MenuItem value={PlanYear.Year2022}>2022</MenuItem>
            <MenuItem value={PlanYear.Year2023}>2023</MenuItem>
            <MenuItem value={PlanYear.Year2024}>2024</MenuItem>
            <MenuItem value={PlanYear.Year2025}>2025</MenuItem>
          </SelectInput>
        </Stack>
      </PageTitle>

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={total}
                loading={loading}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={rows}/>
    </ListPageWrapper>
  );
};

export const Files = withTableSettings(Page, 'files')

const AddDocButton = ({bidId, field, year, exists}: {bidId: string, field: string, year: PlanYear, exists: boolean}) => {
  const [showCheck, setShowCheck] = useState(exists);
  const [showLoading, setShowLoading] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append('file', (e?.target?.files as any)[0]);

    const requestOptions = {
      method: 'POST',
      headers: {
      },
      body: formData
    };

    const url = environment.fileUploadUrl + `${year}/${bidId}/${field}`

    setShowLoading(true);
    fetch(url, requestOptions)
      .then(() => setShowCheck(true))
      .catch(() => enqueueSnackbar('Error in uploading file!', {variant: "error"}))
  }

  const id = bidId + field + year;

  if (showCheck) {
    return <Check weight={'bold'} color={'green'} size={16} style={{marginLeft: 4, marginTop: 6}} />
  }

  if (showLoading) {
    return <CircularProgress size={15} />
  }

  return <>
    <input accept="application/pdf,application/vnd.ms-excel" onChange={onChange} style={{display: 'none'}} id={id} type="file" />
    <label htmlFor={id}>
      <IconButton component="span" size={'small'}>
        <FilePlus weight={'duotone'} />
      </IconButton>
    </label>
  </>
}

const tableColumns: DataGridColumn<ManuallyUploadedPlanFileOutput, any>[] = [
  {
    title: 'Bid Id',
    value: v => v.bidId
  },
  {
    title: 'Evidence',
    value: v => (
      <AddDocButton exists={!!v.evidenceOfCoverageDocUrl}
                    year={v.planYear}
                    bidId={v.bidId} field={'evidenceOfCoverageDoc'} />
    )
  },
  {
    title: 'Nondiscrimination',
    value: v => (
      <AddDocButton exists={!!v.nondiscriminationNoticeDocUrl}
                    year={v.planYear}
                    bidId={v.bidId} field={'nonDiscriminationNoticeDoc'} />
    )
  },
  {
    title: 'Pre Enrollment',
    value: v => (
      <AddDocButton exists={!!v.preEnrollmentChecklistDocUrl}
                    year={v.planYear}
                    bidId={v.bidId} field={'preEnrollmentChecklistDocUrl'} />
    )
  },
  {
    title: 'Star Rating',
    value: v => (
      <AddDocButton exists={!!v.starRatingDescDocUrl}
                    year={v.planYear}
                    bidId={v.bidId} field={'starRatingDescDocUrl'} />
    )
  },
  {
    title: 'Summary Of Benefits',
    value: v => (
      <AddDocButton exists={!!v.summaryOfBenefitsUrl}
                    year={v.planYear}
                    bidId={v.bidId} field={'summaryOfBenefitsUrl'} />
    )
  },
  {
    title: 'Plan Name',
    value: v => v.planName
  },
  {
    title: 'Org Name',
    value: v => v.orgName
  },
  {
    title: 'Parent Org Name',
    value: v => v.parentOrgName
  },
  {
    title: 'Zip',
    value: v => v.zip
  },
  {
    title: 'Year',
    value: v => v.planYear
  },
]

const useFilters = () => {
  return useSessionStorageState<Filters>(
    'filesListPlanYear',
    initialFilters
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'fileListPage',
    { page: 0, size: 20 }
  );
};
